import React, { useEffect, useMemo, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import FullCalendar from '@fullcalendar/react';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import interactionPlugin from '@fullcalendar/interaction';
import huLocale from '@fullcalendar/core/locales/hu';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import useParam from '../../../../context/ParamContext';
import { useLoader } from '../../../../provider/LoaderProvider';
import simpletaskService from '../../../../service/simpletask.service';
import { ColorPicker } from '../../../../config/ColorPicker';
import EventChip from './EventChip';
import addNotification from '../../../../utils/addNotification';
import { NotificationType } from '../../../../config';
import SimpleTaskDialog from './SimpleTaskDialog';
import useUsers from '../../../../context/UsersContext';
import useMenus from '../../../../context/MenuContext';
import dvTaskresourcePlannerService from '../../../../service/client/delVill/dvtaskResourcePlanner.service';
import SearchDialog from './SearchDialog';
import {
  AutoCompleteSelectUI,
  CheckboxUI,
  ConfirmDialogUI,
  DebouncedInputUI,
  DropDownUI,
  FormLabelUI,
  InputUI,
  SelectUI,
} from '../../../Interface';
import { Box, Grid } from '@mui/material';
import masterDataService from '../../../../service/masterData.service';
import { generateAutocompleteItems, generateDefaultTime } from '../../../../utils/helper';
import useConfig from '../../../../context/ConfigContext';
import GroupDialog from './GroupDialog';
import { BatchOptionList, BatchOption } from '../../../../config/client/delVill/task.resource.planner';
import DateDialog from './DateDialog';

const DVTaskResourcePlanner = () => {
  const checkboxFiltersRef = useRef(null);
  const isDraggingEventRef = useRef(false);
  const { config, getConfig } = useConfig();
  const calendarRef = useRef(null);
  const { getUser, user } = useUsers();
  const { getParam, params } = useParam();
  const { menus, getMenus, getSubMenuNamesByUrls } = useMenus();
  const { showLoader, hideLoader } = useLoader();
  const [tasks, setTasks] = useState({ resources: [], taskEvents: [], backgroundEvents: [] });
  const [dialogOpen, setDialogOpen] = useState({ open: false, editing: false });
  const [simpleTaskDialogOpen, setSimpleTaskDialogOpen] = useState(false);
  const [task, setTask] = useState({});
  const [dateRange, setDateRange] = useState({});
  const [taskChanged, setTaskChanged] = useState(false);
  const [timeInterval, setTimeInterval] = useState({});
  const [intervalButtons, setIntervalButtons] = useState({});
  const [calendarViews, setCalendarViews] = useState({});
  const [selectedView, setSelectedView] = useState('');
  const [dialogData, setDialogData] = useState({});
  const [autocompleteData, setAutocompleteData] = useState({ project: [], resourceFour: [] });
  const [autoCompleteOptions, setAutoCompleteOptions] = useState({ project: [], resourceFour: [] });
  const [resourceFilter, setResourceFilter] = useState({ resourceField: '' });
  const [projectFilter, setProjectFilter] = useState(null);
  const [resourceFourFilter, setResourceFourFilter] = useState(null);
  const [defResources, setDefResources] = useState([]);
  const [defTasks, setDefTasks] = useState([]);
  const [copyEnabled, setCopyEnabled] = useState(false);
  const [selectedBatchOption, setSelectedBatchOption] = useState('');
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [dateDialogOpen, setDateDialogOpen] = useState(false);

  const defGroupDialogData = {
    open: false,
    task: {},
  };
  const [groupDialogData, setGroupDialogData] = useState({
    defGroupDialogData,
  });

  // add: search state
  const [searchQuery, setSearchQuery] = useState('');
  // add: search dialog open state
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  // add: double-click helpers
  const clickTimerRef = useRef(null);
  const lastClickRef = useRef({ id: null, ts: 0 });
  // add: date double-click helpers
  const dateClickTimerRef = useRef(null);
  const lastDateClickRef = useRef({ key: null, ts: 0 });
  // add: header offset for fixed desktop header
  const [headerOffset, setHeaderOffset] = useState(0);
  const [copyId, setCopyId] = useState(null);

  const defConfirmDialogData = {
    open: false,
    message: '',
    handleChangeElement: () => {},
  };
  const [confirmDialogData, setConfirmDialogData] = useState(defConfirmDialogData);

  const slotIntervals = {
    halfHour: { hours: 0.5 },
    hour: { hours: 1 },
    day: { days: 1 },
    week: { weeks: 1 },
    month: { months: 1 },
  };

  const calendarHourViews = {
    resourceTimelineDay: {
      slotLabelFormat: [{ hour: 'numeric', minute: '2-digit' }],
    },
    resourceTimelineWeek: {
      slotLabelFormat: [
        { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' },
        { hour: 'numeric', minute: '2-digit' },
      ],
    },
    resourceTimelineMonth: {
      slotLabelFormat: [
        { day: 'numeric', weekday: 'long' },
        { hour: 'numeric', minute: '2-digit' },
      ],
    },
  };

  const calendarDayViews = {
    resourceTimelineDay: {
      slotLabelFormat: [],
    },
    resourceTimelineWeek: {
      slotLabelFormat: [{ day: 'numeric', weekday: 'long' }],
    },
    resourceTimelineMonth: {
      slotLabelFormat: [{ day: 'numeric', weekday: 'short' }],
    },
    resourceTimelineYear: {
      slotLabelFormat: [{ month: 'short' }, { day: 'numeric' }],
    },
  };

  const calendarWeekViews = {
    resourceTimelineDay: {
      slotLabelFormat: [],
    },
    resourceTimelineWeek: {
      slotLabelFormat: [{ week: 'numeric' }],
    },
    resourceTimelineMonth: {
      slotLabelFormat: [{ week: 'numeric' }],
    },
    resourceTimelineYear: {
      slotLabelFormat: [{ week: 'numeric' }],
    },
  };

  const calendarMonthViews = {
    resourceTimelineDay: {
      slotLabelFormat: [],
    },
    resourceTimelineWeek: {
      slotLabelFormat: [],
    },
    resourceTimelineMonth: {
      slotLabelFormat: [],
    },
    resourceTimelineYear: {
      slotLabelFormat: [{ month: 'long' }],
    },
  };

  const initDefaultValues = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .projectToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .resourceFourToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          project: resp[0],
          resourceFour: resp[1],
        };
        setAutocompleteData(aData);

        const acOptions = {
          project: generateAutocompleteItems(aData.project, 'projectName', 'projectId'),
          resourceFour: generateAutocompleteItems(aData.resourceFour, 'name', 'id'),
        };
        setAutoCompleteOptions(acOptions);
      })
      .finally(() => {
        //hideLoader();
      });
  };

  useEffect(() => {
    initDefaultValues();
    getConfig();

    const toolbarElement = document.querySelector('.fc-toolbar-chunk');
    if (toolbarElement && checkboxFiltersRef.current) {
      toolbarElement.appendChild(checkboxFiltersRef.current);
    }
  }, []);

  const refreshCalendar = () => {
    const calendarApi = calendarRef.current.getApi();
    setTimeout(() => {
      calendarApi.render();
    }, 100);
  };

  useEffect(() => {
    refreshCalendar();
  }, [tasks.taskEvents]);

  useEffect(() => {
    dayjs.extend(utc);
    dayjs.extend(timezone);
    setTimeInterval(slotIntervals.day);
    setCalendarViews(calendarDayViews);

    // @TODO promiseArray
    getResources();
    getParam();
    getMenus();
    if (!Object.keys(user).length) getUser();
  }, []);

  useEffect(() => {
    const indicator = document.querySelector('.fc-now-indicator');
    if (!indicator) return;

    const getScrollableParent = (el) => {
      let p = el?.parentElement;
      while (p) {
        const cs = getComputedStyle(p);
        const canScrollX = /(auto|scroll)/.test(cs.overflowX);
        if (canScrollX && p.scrollWidth > p.clientWidth) return p;
        p = p.parentElement;
      }
      return null;
    };

    const container = getScrollableParent(indicator);

    if (!container) {
      // Fallback: try built-in centering
      indicator.scrollIntoView({ block: 'nearest', inline: 'center' });
      return;
    }

    requestAnimationFrame(() => {
      const containerRect = container.getBoundingClientRect();
      const indicatorRect = indicator.getBoundingClientRect();

      // indicator center position relative to container scroll origin
      const indicatorCenterX = indicatorRect.left - containerRect.left + container.scrollLeft + indicatorRect.width / 2;

      const desiredLeft = Math.max(0, indicatorCenterX - container.clientWidth / 2);
      const maxScrollLeft = Math.max(0, container.scrollWidth - container.clientWidth);

      // If centering would pass the right edge, go fully right
      container.scrollLeft = desiredLeft >= maxScrollLeft - 1 ? maxScrollLeft : desiredLeft;
    });
  }, [timeInterval, calendarViews, dateRange.startDate, dateRange.endDate]);

  // add: update header offset based on breakpoint (lg: 1024px)
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const update = () => setHeaderOffset(mq.matches ? 64 : 0);
    update();
    if (mq.addEventListener) {
      mq.addEventListener('change', update);
      return () => mq.removeEventListener('change', update);
    } else {
      mq.addListener(update);
      return () => mq.removeListener(update);
    }
  }, []);

  useEffect(() => {
    setIntervalButtons(generateButtons(selectedView));
  }, [selectedView]);

  useEffect(() => {
    if (taskChanged) {
      getTasks();
      setTaskChanged(false);
    }
  }, [taskChanged]);

  useEffect(() => {
    if (Object.keys(user).length > 0 && dateRange.startDate && dateRange.endDate) getTasks();
  }, [dateRange.startDate, dateRange.endDate, user]);

  const getResources = () => {
    showLoader();

    const body = {
      ...resourceFilter,
    };

    dvTaskresourcePlannerService
      .getResources(body)
      .then((data) => {
        setTasks((prevState) => ({ ...prevState, resources: data }));
        setDefResources(data);
      })
      .finally(() => {
        hideLoader();
      });
  };

  useEffect(() => {
    if (Object.keys(user).length > 0) {
      getTasks();
    }
  }, [projectFilter, user.userId]);

  const filterByResourceFour = () => {
    const filteredMainTasks = defTasks.filter((task) => task.eventData.resourceFourId === resourceFourFilter);

    const filteredTasks = [
      ...filteredMainTasks,
      ...defTasks.filter((task) => filteredMainTasks.some((mt) => mt.id === task.eventData.parentId)),
    ];

    const filteredResourceIds = new Set(filteredTasks.map((task) => task.resourceId));
    setTasks((prev) => ({
      ...prev,
      resources: defResources.filter((resource) => filteredResourceIds.has(resource.id)),
      taskEvents: filteredTasks,
    }));
  };

  useEffect(() => {
    if (!resourceFourFilter) {
      setTasks((prev) => ({
        ...prev,
        resources: defResources,
        taskEvents: defTasks,
      }));
      return;
    }

    filterByResourceFour();
  }, [resourceFourFilter]);

  useEffect(() => {
    if (resourceFourFilter) {
      filterByResourceFour();
    }
  }, [defTasks]);

  useEffect(() => {
    if (resourceFilter.resourceField === null || resourceFilter.resourceField.trim() === '') {
      setTasks((prev) => ({
        ...prev,
        resources: defResources,
      }));
    } else {
      setTasks((prev) => ({
        ...prev,
        resources: prev.resources.filter((resource) =>
          resource.title.toLowerCase()?.includes(resourceFilter.resourceField?.trim().toLowerCase())
        ),
      }));
    }
  }, [resourceFilter]);

  const submenuNames = useMemo(() => {
    return getSubMenuNamesByUrls(['simpleTask']);
  }, [menus]);

  const colorParam = params.TASKPLANNER_CHIP_COLOR_THEME;
  const parsedParam = colorParam ? JSON.parse(colorParam) : [];

  // add: open/close dialog based on query
  useEffect(() => {
    if (searchQuery && searchQuery.trim().length > 0) {
      setSearchDialogOpen(true);
    } else {
      setSearchDialogOpen(false);
    }
  }, [searchQuery]);

  const getTasks = () => {
    const body = {
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      ...(projectFilter && { projectId: projectFilter }),
    };
    //showLoader();
    dvTaskresourcePlannerService
      .getTask(body)
      .then((data) => {
        const taskEvents = data.map((task) => {
          const colorStyles = getColorStyles(task?.eventData, parsedParam[0], 'TASK');
          return {
            ...task,
            ...colorStyles,
          };
        });

        setDefTasks(taskEvents);
        setTasks((prevState) => ({ ...prevState, taskEvents }));
      })
      .finally(() => {
        hideLoader();
      });
  };

  const getTaskById = (simpleTaskId, editing = false) => {
    showLoader();
    simpletaskService
      .getTaskById(simpleTaskId)
      .then((data) => {
        setTask(data);
        setDialogOpen({ open: true, editing });
      })
      .finally(() => {
        hideLoader();
      });
  };

  const getColorStyles = (event, colorProperties, eventType) => {
    const defaultStyles = { backgroundColor: '#fff', textColor: '#000' };

    const colorProperty = colorProperties?.[eventType];
    if (!colorProperty) return defaultStyles;

    const colorName = colorProperty.split('.').reduce((obj, key) => obj?.[key], event);
    if (!colorName) return defaultStyles;

    const colorObj = ColorPicker.find((color) => color.colorName === colorName);
    return {
      backgroundColor: colorObj?.colorBgCode || defaultStyles.backgroundColor,
      textColor: colorObj?.textColor || defaultStyles.textColor,
    };
  };

  const handleDatesSet = (dateInfo) => {
    const startDate = dayjs(dateInfo.start).startOf('day');
    const endDate = dayjs(dateInfo.end).endOf('day');

    if (
      !dateRange.startDate ||
      !dateRange.endDate ||
      !startDate.isSame(dateRange.startDate) ||
      !endDate.isSame(dateRange.endDate)
    ) {
      setDateRange({ startDate, endDate });
    }

    if (selectedView !== dateInfo.view.type) setSelectedView(dateInfo.view.type);
  };

  const validateEventDrop = (eventInfo) => {
    const { event, revert, oldEvent } = eventInfo;
    const resourceObj = event.getResources()[0];
    const oldres = oldEvent.getResources()[0].id;
    const oldStart = dayjs(oldEvent.start);

    const resourceType = resourceObj.extendedProps?.type;

    const resData = resourceObj.id.split('_');
    const oldresData = oldres.split('_');

    if (oldresData[0] !== resData[0]) {
      addNotification({
        content: 'Nem helyezhető át másik erőforrás csoportra!',
        type: NotificationType.WARNING,
      });
      revert();
      return false;
    }

    // a napot ne lehessen módosítani
    if (resourceType !== 'resourceFour' && !dayjs(event.start).isSame(oldStart, 'day')) {
      addNotification({
        content: 'A nap módosítása nem engedélyezett!',
        type: NotificationType.WARNING,
      });
      revert();
      return false;
    }

    return true;
  };

  const updateEvent = (eventInfo) => {
    const { event, revert, oldEvent } = eventInfo;
    const resource = event.getResources()[0].id;
    const resData = resource.split('_');

    const oldStart = dayjs(oldEvent.start);
    const durationInMinutes = event.end ? dayjs(event.end).diff(dayjs(event.start), 'minute') : null;
    const duration = durationInMinutes / 60;
    const startDate = dayjs(event.start);
    const endDate = event.end
      ? dayjs(event.end).toISOString()
      : dayjs(event.start).add(durationInMinutes, 'minute').toISOString();

    const resourceId = parseInt(resData[1]);

    const updatedEventData = {
      resourceId,
      //startDate,
      //endDate,
      resourceField: resource.startsWith('b_') ? 'resourceTwoId' : 'resourceFourId',
    };

    if (!startDate.isSame(oldStart, 'day')) {
      updatedEventData.startDate = startDate.toISOString();
      updatedEventData.endDate = endDate;
    }

    //showLoader();

    const simpleTaskId = event.id;

    updatedEventData.duration = duration;
    dvTaskresourcePlannerService
      .updateTask(updatedEventData, simpleTaskId)
      .then((data) => {
        addNotification({
          content: 'Sikeres módosítás!',
          type: NotificationType.SUCCESS,
        });
        onCloseConfirmDialog();
        setTaskChanged(true);
      })
      .catch(() => {
        revert();
      });
  };

  const timeIntervalButtons = {
    halfHour: {
      text: 'Fél óra',
      click: () => {
        setTimeInterval(slotIntervals.halfHour);
        setCalendarViews(calendarHourViews);
      },
    },
    hour: {
      text: 'Óra',
      click: () => {
        setTimeInterval(slotIntervals.hour);
        setCalendarViews(calendarHourViews);
      },
    },
    day: {
      text: 'Nap',
      click: () => {
        setTimeInterval(slotIntervals.day);
        setCalendarViews(calendarDayViews);
      },
    },
    week: {
      text: 'Hét',
      click: () => {
        setTimeInterval(slotIntervals.week);
        setCalendarViews(calendarWeekViews);
      },
    },
    month: {
      text: 'Hónap',
      click: () => {
        setTimeInterval(slotIntervals.month);
        setCalendarViews(calendarMonthViews);
      },
    },
  };

  const generateButtons = (viewName) => {
    let retVal = {};
    switch (viewName) {
      case 'resourceTimelineDay':
        retVal = 'halfHour,hour,day';
        setTimeInterval(slotIntervals.halfHour);
        setCalendarViews(calendarHourViews);

        break;
      case 'resourceTimelineWeek':
        retVal = 'halfHour,hour,day,week';
        setTimeInterval(slotIntervals.day);
        setCalendarViews(calendarDayViews);

        break;
      case 'resourceTimelineMonth':
        retVal = 'hour,day,week,month';
        setTimeInterval(slotIntervals.day);
        setCalendarViews(calendarDayViews);

        break;
      case 'resourceTimelineYear':
        retVal = 'day,week,month';
        setTimeInterval(slotIntervals.day);
        setCalendarViews(calendarDayViews);

        break;
    }
    return retVal;
  };

  const handleDateClick = async (event) => {
    const { resource, date } = event;

    const task = copyId ? await simpletaskService.getTaskById(copyId) : {};

    if (copyId) {
      setCopyId(null);
    }

    const startDate = generateDefaultTime(date, config.defTaskFromTime);

    const duration = config?.defTaskDuration ?? 1;

    const resourceData = resource.id.split('_');

    if (resourceData[0] === 'b') {
      addNotification({
        content: 'Nem hozható létre feladat ezen az erőforrás csoporton!',
        type: NotificationType.WARNING,
      });
      return;
    }

    const { resources, ...taskWithoutResources } = task;

    let dialogData = {
      ...taskWithoutResources,
      startDate,
      endDate: startDate.add(duration, 'hour'),
      duration,
      resourceFourId: parseInt(resourceData[1]),
    };

    setSimpleTaskDialogOpen(true);
    setDialogData(dialogData);
  };

  const handleDeleteTasks = () => {
    dvTaskresourcePlannerService.deleteTasks({ ids: selectedTaskIds }).then(() => {
      setTaskChanged(true);
      setSelectedBatchOption('');
      setSelectedTaskIds([]);
      setConfirmDialogData(defConfirmDialogData);
    });
  };

  const onClearAfterOperation = () => {
    setTaskChanged(true);
    onCloseConfirmDialog();
    setDateDialogOpen(false);
    setSelectedBatchOption('');
    setSelectedTaskIds([]);
  };

  const handleCopyTasks = (body) => {
    dvTaskresourcePlannerService.copyTasks(body).then(() => {
      onClearAfterOperation();
    });
  };

  const handleMoveTasks = (body) => {
    dvTaskresourcePlannerService.moveTasks(body).then(() => {
      onClearAfterOperation();
    });
  };

  const onCloseConfirmDialog = (withRevert = false) => {
    if (withRevert && confirmDialogData.event?.revert) {
      confirmDialogData.event.revert();
    }
    setConfirmDialogData(defConfirmDialogData);
  };

  const handleSelectBatchOption = (option) => {
    setSelectedBatchOption(option);

    switch (option) {
      case BatchOption.DELETE:
        setConfirmDialogData({
          open: true,
          message: 'Megerősíti a törlési műveletet? Az erőforrásigények is törlése kerülnek!',
          handleChangeElement: () => handleDeleteTasks(),
        });
        break;
      case BatchOption.COPY:
        setDateDialogOpen(true);
        break;
      case BatchOption.MOVE:
        setDateDialogOpen(true);
        break;
    }
  };

  const handleSaveDate = (selectedDate) => {
    const isCopy = selectedBatchOption === BatchOption.COPY;
    const isMove = selectedBatchOption === BatchOption.MOVE;

    const startDate = generateDefaultTime(selectedDate, config.defTaskFromTime);

    const duration = config?.defTaskDuration ?? 1;
    const endDate = startDate.add(duration, 'hour');

    const message = isCopy ? 'Megerősíti a másolási műveletet?' : 'Megerősíti az áthelyezési műveletet?';

    const body = {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      simpleTaskIds: selectedTaskIds,
    };

    setConfirmDialogData({
      open: true,
      message,
      handleChangeElement: () => {
        if (isCopy) handleCopyTasks(body);
        if (isMove) handleMoveTasks(body);
      },
    });
  };

  return (
    <div
      className="taskListCalendar taskPlanner plannerDarkerBorder dvPlannerChip"
      style={{ height: `calc(100dvh - ${headerOffset}px)` }}
    >
      <div ref={checkboxFiltersRef}>
        <Grid container spacing={0.5} sx={{ width: '100%', alignItems: 'center' }}>
          <Grid item sx={{ flex: '1 1 180px', minWidth: 180 }}>
            <InputUI
              type="search"
              label={<FormLabelUI text="Keresés" />}
              variant="standard"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </Grid>
          <Grid item sx={{ flex: '1 1 180px', minWidth: 180 }}>
            <AutoCompleteSelectUI
              key="project"
              name="project"
              label={<FormLabelUI text="Projekt" />}
              onChange={(_e, newVal, reason) => {
                if (reason === 'clear') {
                  setProjectFilter(null);
                } else {
                  setProjectFilter(newVal.value);
                }
              }}
              dataset={autoCompleteOptions.project}
              selectedValue={projectFilter}
              selectedLabelValue={autoCompleteOptions.project.find((item) => item.value === projectFilter)?.label}
            />
          </Grid>
          <Grid item sx={{ flex: '1 1 180px', minWidth: 180 }}>
            <DebouncedInputUI
              label={<FormLabelUI text="Erőforrás" />}
              debounceMs={100}
              setQuickFilterSearchValue={(newValue) => {
                setResourceFilter((prevState) => {
                  if (prevState.resourceField === newValue) {
                    return prevState;
                  }
                  return {
                    ...prevState,
                    resourceField: newValue,
                  };
                });
              }}
              quickFilterSearchValue={resourceFilter.resourceField}
            />
          </Grid>
          <Grid item sx={{ flex: '1 1 180px', minWidth: 180 }}>
            <AutoCompleteSelectUI
              key="group"
              name="group"
              label={<FormLabelUI text="Csoport" />}
              onChange={(_e, newVal, reason) => {
                if (reason === 'clear') {
                  setResourceFourFilter(null);
                } else {
                  setResourceFourFilter(newVal.value);
                }
              }}
              dataset={autoCompleteOptions.resourceFour}
              selectedValue={resourceFourFilter}
              selectedLabelValue={
                autoCompleteOptions.resourceFour.find((item) => item.value === resourceFourFilter)?.label
              }
            />
          </Grid>
          <Grid item sx={{ flex: '1 1 100px', minWidth: 100 }}>
            <Box mt={0.5}>
              <SelectUI
                label={<FormLabelUI text="Műveletek" />}
                option={BatchOptionList}
                onChange={(e) => handleSelectBatchOption(e.target.value)}
                value={selectedBatchOption}
                onClear={() => {
                  setSelectedBatchOption('');
                }}
                disabled={!selectedTaskIds.length}
              />
            </Box>
          </Grid>
          <Grid item>
            <Box mt={1}>
              <CheckboxUI label="Másolás" onChange={() => setCopyEnabled(!copyEnabled)} checked={!!copyEnabled} />
            </Box>
          </Grid>
        </Grid>
      </div>
      <FullCalendar
        ref={calendarRef}
        height="100%"
        contentHeight="100%"
        customButtons={timeIntervalButtons}
        plugins={[resourceTimelinePlugin, interactionPlugin]}
        initialView="resourceTimelineWeek"
        resources={tasks.resources}
        resourceOrder="visOrder"
        locales={[huLocale]}
        locale="hu"
        slotLabelFormat={[
          { month: 'long', year: 'numeric', weekday: 'long' },
          { hour: 'numeric', minute: '2-digit' },
        ]}
        datesSet={handleDatesSet}
        events={[...tasks.taskEvents, ...tasks.backgroundEvents]}
        // disable drag/resize while long-press is active
        editable={true}
        eventStartEditable={true}
        eventDurationEditable={false}
        dateClick={(info) => {
          if (copyId) {
            if (dateClickTimerRef.current) {
              clearTimeout(dateClickTimerRef.current);
              dateClickTimerRef.current = null;
            }
            lastDateClickRef.current = { key: null, ts: 0 };
            handleDateClick(info);
            return;
          }
          // otherwise require double-click
          const now = Date.now();
          const key = `${info.resource?.id ?? ''}-${+info.date}`;
          const last = lastDateClickRef.current;
          if (last.key === key && now - last.ts < 300) {
            if (dateClickTimerRef.current) {
              clearTimeout(dateClickTimerRef.current);
              dateClickTimerRef.current = null;
            }
            lastDateClickRef.current = { key: null, ts: 0 };
            handleDateClick(info);
          } else {
            lastDateClickRef.current = { key, ts: now };
            if (dateClickTimerRef.current) clearTimeout(dateClickTimerRef.current);
            dateClickTimerRef.current = setTimeout(() => {
              lastDateClickRef.current = { key: null, ts: 0 };
            }, 300);
          }
        }}
        nowIndicator={true}
        //eventResize={updateEvent}
        eventDrop={(eventInfo) => {
          setCopyId(null);
          const isValid = validateEventDrop(eventInfo);
          if (!isValid) return;

          const { event, oldEvent } = eventInfo;

          const oldStart = dayjs(oldEvent.start);
          const newStart = dayjs(event.start);

          const dateChanged = !newStart.isSame(oldStart, 'day');

          const message = dateChanged
            ? 'Megerősíti a műveletet? Az erőforrásigények törlése kerülnek!'
            : 'Megerősíti a műveletet?';

          setConfirmDialogData({
            open: true,
            handleChangeElement: () => updateEvent(eventInfo),
            message,
          });
        }}
        eventDragStart={() => {
          isDraggingEventRef.current = true;
        }}
        eventDragStop={() => {
          isDraggingEventRef.current = false;
        }}
        eventClick={(clickInfo) => {
          // handle only double-clicks, ignore background events
          if (clickInfo.event.display === 'background') return;

          const event = clickInfo.event;
          const id = parseInt(event.id);
          const now = Date.now();

          const last = lastClickRef.current;
          const isSameEvent = last.id === event.id;

          const isDouble = isSameEvent && now - last.ts < 300;

          if (isDouble) {
            if (clickTimerRef.current) {
              clearTimeout(clickTimerRef.current);
              clickTimerRef.current = null;
            }
            lastClickRef.current = { id: null, ts: 0 };

            if (params.TASKPLANNER_DOUBLE_CLICK_TASK_ENABLE === 'Y') {
              getTaskById(id, true);
            } else {
              getTaskById(id);
            }
            return;
          }

          lastClickRef.current = { id: event.id, ts: now };

          if (copyEnabled && !event.extendedProps.eventData.resourceTwoId) {
            if (clickTimerRef.current) clearTimeout(clickTimerRef.current);

            clickTimerRef.current = setTimeout(() => {
              setGroupDialogData({
                open: true,
                task: event.extendedProps.eventData,
              });
              lastClickRef.current = { id: null, ts: 0 };
            }, 300);

            return;
          }

          if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
          clickTimerRef.current = setTimeout(() => {
            lastClickRef.current = { id: null, ts: 0 };
          }, 300);
        }}
        eventContent={(eventInfo) => {
          if (eventInfo.event.display !== 'background') {
            return (
              <EventChip
                eventInfo={eventInfo}
                setCopyId={setCopyId}
                isDraggingRef={isDraggingEventRef}
                selectedTaskState={{ selectedTaskIds, setSelectedTaskIds }}
              />
            );
          }
        }}
        eventClassNames={(arg) => {
          if (arg.event.display === 'background') return [];
          return selectedTaskIds.includes(Number(arg.event.id)) ? ['fc-task-selected'] : [];
        }}
        headerToolbar={{
          left: `prev,next,today ${intervalButtons}`,
          center: 'title',
          right: 'resourceTimelineDay,resourceTimelineWeek,resourceTimelineMonth,resourceTimelineYear',
        }}
        views={calendarViews}
        slotDuration={timeInterval}
        resourceAreaWidth="15%"
        resourceAreaHeaderContent="Erőforrás"
        scrollTimeReset={false}
        resourceLabelClassNames={(data) => {
          if (!data.resource._resource.parentId) {
            return ['fc-res-group'];
          }
        }}
        slotMinWidth={180}
        nowIndicatorClassNames="fc-now-indicator"
      />

      <SearchDialog
        open={searchDialogOpen}
        searchQuery={searchQuery}
        handleClose={() => setSearchDialogOpen(false)}
        getTaskById={getTaskById}
        onSearchChange={setSearchQuery}
      />

      <SimpleTaskDialog
        open={dialogOpen.open}
        changed={{ taskChanged, setTaskChanged }}
        task={task}
        editing={dialogOpen.editing}
        submenuNames={submenuNames}
        handleClose={() => {
          setDialogOpen({ open: false, editing: false });
        }}
      />

      <SimpleTaskDialog
        changed={{ taskChanged, setTaskChanged }}
        open={simpleTaskDialogOpen}
        submenuNames={submenuNames}
        dialogData={dialogData}
        handleClose={() => {
          setSimpleTaskDialogOpen(false);
        }}
      />

      <ConfirmDialogUI
        handleChangeElement={confirmDialogData.handleChangeElement}
        show={confirmDialogData.open}
        onHide={() => onCloseConfirmDialog(true)}
        message={confirmDialogData.message}
      />

      <GroupDialog
        groupDialogData={groupDialogData}
        handleClose={() => setGroupDialogData(defGroupDialogData)}
        groups={autoCompleteOptions.resourceFour}
        setTaskChanged={setTaskChanged}
      />

      <DateDialog open={dateDialogOpen} handleClose={() => setDateDialogOpen(false)} onSave={handleSaveDate} />
    </div>
  );
};

export default DVTaskResourcePlanner;
